import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
  Stack,
  Tooltip,
  Pagination,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import accouncementIcon from "../../images/accouncement.png";
import { env } from "../../../utilities/function";
import { getCookie } from "../../../utilities/cookies";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import pageOptions from "../../../constants/pageOptions";

const API_BASE_URL = env("SERVER");

// API Functions
const fetchAnnouncements = async (page = 1, limit = 100, filters = {}) => {
  const accessToken = getCookie("accessToken");
  console.log("Fetching announcements with token:", accessToken);

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...filters,
  });

  const response = await axios.get(`${API_BASE_URL}/announcements?${params}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

const createAnnouncement = async (data) => {
  const accessToken = getCookie("accessToken");
  console.log("Creating announcement with token:", accessToken);

  // Clean data to only include allowed fields
  const cleanData = {
    name: data.name,
    text: data.text,
    pageName: data.pageName,
    onClick: data.onClick || null,
    isEnabled: data.isEnabled,
  };

  return await axios.post(`${API_BASE_URL}/announcements`, cleanData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const updateAnnouncement = async (id, data) => {
  const accessToken = getCookie("accessToken");
  console.log("Updating announcement with token:", accessToken);

  // Clean data to only include allowed fields
  const cleanData = {
    name: data.name,
    text: data.text,
    pageName: data.pageName,
    onClick: data.onClick || null,
    isEnabled: data.isEnabled,
  };

  return await axios.put(`${API_BASE_URL}/announcements/${id}`, cleanData, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const deleteAnnouncement = async (id) => {
  const accessToken = getCookie("accessToken");
  console.log("Deleting announcement with token:", accessToken);

  return await axios.delete(`${API_BASE_URL}/announcements/${id}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

const toggleAnnouncementStatus = async (id, isEnabled) => {
  const accessToken = getCookie("accessToken");
  console.log("Toggling announcement status with token:", accessToken);

  return await axios.patch(
    `${API_BASE_URL}/announcements/${id}/toggle`,
    { isEnabled },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

const fetchAnnouncementActivity = async () => {
  const accessToken = getCookie("accessToken");
  console.log("Fetching announcement activity with token:", accessToken);

  const response = await axios.get(`${API_BASE_URL}/announcements/activity`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

const AnnouncementForm = ({
  open,
  onClose,
  announcement,
  onSubmit,
  isEditing,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    text: "",
    pageName: "",
    onClick: "",
    isEnabled: true,
  });

  useEffect(() => {
    if (announcement) {
      setFormData({
        name: announcement.name || "",
        text: announcement.text || announcement.content || "",
        pageName: announcement.pageName || "",
        onClick: announcement.onClick || "",
        isEnabled: announcement.isEnabled ?? true,
      });
    } else {
      setFormData({
        name: "",
        text: "",
        pageName: "",
        onClick: "",
        isEnabled: true,
      });
    }
  }, [announcement, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };



  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "link",
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        backgroundColor: "transparent",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? "Edit Announcement" : "Create New Announcement"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Announcement Name"
                value={formData.name}
                onChange={handleChange("name")}
                required
                helperText="A descriptive name for this announcement"
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={formData.text}
                  onChange={(html) =>
                    setFormData((prev) => ({ ...prev, text: html || "" }))
                  }
                  modules={quillModules}
                  formats={quillFormats}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                The message to display to users
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Page Name</InputLabel>
                <Select
                  value={formData.pageName}
                  onChange={handleChange("pageName")}
                  required
                >
                  {pageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  Select the page where this announcement should appear
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="onClick Action (Optional)"
                value={formData.onClick}
                onChange={handleChange("onClick")}
                placeholder="e.g., window.open('https://example.com')"
                helperText="JavaScript code to execute when announcement is clicked"
              />
            </Grid>


            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isEnabled}
                    onChange={handleChange("isEnabled")}
                  />
                }
                label="Enable Announcement"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Preview
              </Typography>
              <Box
                sx={{
                  backgroundColor: "black",
                  color: "white",
                  p: 2,
                  borderRadius: 1,
                  textAlign: "center",
                  cursor: formData.onClick ? "pointer" : "default",
                }}
                onClick={
                  formData.onClick
                    ? () => {
                        try {
                          eval(formData.onClick);
                        } catch (e) {
                          console.log("Preview onClick error:", e);
                        }
                      }
                    : undefined
                }
              >

          <Box
            component="div"
            sx={{
              textAlign: { xs: "left", md: "center" },
              fontSize: { xs: "10px", md: "14px" },
              lineHeight: 1.3,
              width: { xs: "280px", md: "auto" },
              display: "flex",
              justifyContent: { xs: "center", md: "center" },
              alignItems: { xs: "center", md: "center" },
              gap: 1,
            }}
          >
            {formData.text && (
              <>
                <span
                  data-announcement
                  className="prose prose-neutral text-center my-1"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize( formData.text ||
                          "Your announcement text will appear here..."),
                  }}
                />
              </>
            )}
          </Box>
                {formData.onClick && (
                  <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
                    Click to test action
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Announcements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [filters, setFilters] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch announcements
  const {
    data: announcementsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["announcements", page, limit, filters],
    queryFn: () => fetchAnnouncements(page, limit, filters),
    keepPreviousData: true,
  });

  // Fetch activity data
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ["announcementActivity"],
    queryFn: fetchAnnouncementActivity,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  console.log(
    "Announcements data:",
    announcementsData,
    "Loading:",
    isLoading,
    "Error:",
    error,
    "Data length:",
    announcementsData?.data?.length
  );

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries(["announcements"]);
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Announcement created successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error creating announcement: ${error.message}`,
        severity: "error",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateAnnouncement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["announcements"]);
      setDialogOpen(false);
      setEditingAnnouncement(null);
      setSnackbar({
        open: true,
        message: "Announcement updated successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating announcement: ${error.message}`,
        severity: "error",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries(["announcements"]);
      setSnackbar({
        open: true,
        message: "Announcement deleted successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting announcement: ${error.message}`,
        severity: "error",
      });
    },
  });

  // Toggle status mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }) => toggleAnnouncementStatus(id, !isEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries(["announcements"]);
      setSnackbar({
        open: true,
        message: "Announcement status updated!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating status: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleCreate = () => {
    setEditingAnnouncement(null);
    setDialogOpen(true);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleToggle = (id) => {
    const announcement = announcementsData?.data?.find((a) => a._id === id);
    if (announcement) {
      toggleMutation.mutate({ id, isEnabled: announcement.isEnabled });
    }
  };

  const handleSubmit = (formData) => {
    if (editingAnnouncement) {
      updateMutation.mutate({ id: editingAnnouncement._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading announcements: {error.message}
      </Alert>
    );
  }

  const mergedData = announcementsData?.data?.map((announcement) => {
    const activity = activityData?.data?.activityByPage?.find(
      (a) => a.pageName === announcement.pageName
    );
    return {
      ...announcement,
      requestCount: activity?.requestCount || 0,
      lastAccessed: activity?.lastAccessed,
    };
  });

  return (
    <Box
      sx={{
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(4),
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={"bold"} gutterBottom>
            Announcements Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage announcements for Clikkle Website.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size={isMobile ? "small" : "medium"}
        >
          Create Announcement
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CampaignIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    {announcementsData?.pagination?.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Announcements
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ViewIcon color="success" />
                <Box>
                  <Typography variant="h6">
                    {announcementsData?.data?.filter((a) => a.isEnabled)
                      ?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <VisibilityOffIcon color="warning" />
                <Box>
                  <Typography variant="h6">
                    {announcementsData?.data?.filter((a) => !a.isEnabled)
                      ?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AnalyticsIcon color="info" />
                <Box>
                  <Typography variant="h6">
                    {activityLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      activityData?.data?.totalRequests || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Announcement views
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell>Page</TableCell>
                  <TableCell>Click Action</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Announcement views</TableCell>
                  <TableCell>Last Accessed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mergedData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No announcements found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  mergedData?.map((announcement) => (
                    <TableRow key={announcement._id}>
                      <TableCell>{announcement.name}</TableCell>
                      <TableCell     dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(announcement.text),
                  }}></TableCell>
                      <TableCell>{announcement.pageName || "Home"}</TableCell>
                      <TableCell> 
                        {announcement.onClick ? (
                          <Chip
                            label="Has Action"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label="No Action"
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={announcement.isEnabled ? "Active" : "Inactive"}
                          size="small"
                          color={announcement.isEnabled ? "success" : "default"}
                          variant={
                            announcement.isEnabled ? "filled" : "outlined"
                          }
                        />
                      </TableCell>
                      <TableCell>{announcement.requestCount}</TableCell>
                      <TableCell>
                        {announcement.lastAccessed
                          ? new Date(announcement.lastAccessed).toLocaleString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip
                            title={
                              announcement.isEnabled ? "Disable" : "Enable"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleToggle(announcement._id)}
                              color={
                                announcement.isEnabled ? "success" : "default"
                              }
                            >
                              {announcement.isEnabled ? (
                                <ViewIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(announcement)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(announcement._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {announcementsData?.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={announcementsData.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <AnnouncementForm
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingAnnouncement(null);
        }}
        announcement={editingAnnouncement}
        onSubmit={handleSubmit}
        isEditing={!!editingAnnouncement}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Announcements;
